import axios from 'axios';
import { toast } from 'sonner';
import { SELECTED_BUSINESS_KEY } from '@/stores/merchantStore';

const TOKEN_KEY = 'bizpos_token';
const AUTH_COOKIE = 'bizpos_auth';
export const PORTAL_TOKEN_KEY = 'bizpos_portal_token';

export const getPortalToken = (): string | null =>
  typeof window !== 'undefined' ? localStorage.getItem(PORTAL_TOKEN_KEY) : null;

export const setPortalToken = (token: string): void => {
  localStorage.setItem(PORTAL_TOKEN_KEY, token);
};

export const clearPortalToken = (): void => {
  localStorage.removeItem(PORTAL_TOKEN_KEY);
};

export const getStoredToken = (): string | null =>
  typeof window !== 'undefined' ? localStorage.getItem(TOKEN_KEY) : null;

export const setStoredToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
  document.cookie = `${AUTH_COOKIE}=1; path=/; SameSite=Lax`;
};

export const clearStoredToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  document.cookie = `${AUTH_COOKIE}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
};

export const apiClient = axios.create({
  baseURL: (process.env.NEXT_PUBLIC_API_BASE_URL ?? '') + '/api/v1',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else if (typeof window !== 'undefined') {
    const staffToken = localStorage.getItem('bizpos_staff_token');
    if (staffToken) {
      config.headers.Authorization = `Bearer ${staffToken}`;
    }
  }

  const activeBranchId =
    typeof window !== 'undefined'
      ? localStorage.getItem('bizpos_staff_branch_id')
      : null;

  if (activeBranchId && !config.headers['X-Branch-ID']) {
    config.headers['X-Branch-ID'] = activeBranchId;
    config.headers['X-Location-ID'] = activeBranchId;
  }

  const needsBusinessContext =
    config.url?.includes('/merchant/') ||
    config.url?.includes('/auth/me/capabilities');

  if (needsBusinessContext) {
    const selectedBusinessId =
      typeof window !== 'undefined'
        ? localStorage.getItem(SELECTED_BUSINESS_KEY)
        : null;

    if (selectedBusinessId && !config.headers['X-Business-ID']) {
      config.headers['X-Business-ID'] = selectedBusinessId;
    }
  }

  return config;
});

apiClient.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response?.status === 403) {
      toast.error('Action not allowed', {
        description:
          error.response.data?.message ??
          'You do not have permission to perform this action.',
      });
    }
    if (error.response?.status === 401) {
      clearStoredToken();
      if (typeof window !== 'undefined') {
        window.location.replace('/login');
      }
    }
    return Promise.reject(error);
  },
);

export const portalClient = axios.create({
  baseURL: (process.env.NEXT_PUBLIC_API_BASE_URL ?? '') + '/api/v1',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
});

portalClient.interceptors.request.use((config) => {
  const token = getPortalToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
